import React, { FC, memo, useCallback, useEffect, useState } from 'react';
import { Button, Card, Grid, TextField, Typography } from '@material-ui/core';
import { useTaxCertificate } from 'src/hooks/tax-certificate';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { dateToday, transformerDateField } from 'src/utils';
import { GetTaxCertificateListPayload } from 'src/redux/slices/purchase-order/tax-certificate/types';
import QuarterDatePicker from 'src/components/date/QuarterDatePicker';
import { DatePickerRangeComponent } from 'src/components';
import { Autocomplete } from '@material-ui/lab';
import { useSupplier } from 'src/hooks/supplier';
import { Supplier } from 'src/types/supplier';

interface Props {
  onHandleAppliedFilter: () => void;
}

const Component: FC<Props> = ({ onHandleAppliedFilter }) => {
  const {
    //   taxCertificateListParams,
    fetchTaxCertificateList
  } = useTaxCertificate();
  const { suppliers, isSuppliersLoading, getSuppliers } = useSupplier();

  const [fromPeriod, setFromPeriod] = useState<MaterialUiPickersDate | null>(
    null
  );
  const [toPeriod, setToPeriod] = useState<MaterialUiPickersDate | null>(null);
  const [toCreatedAt, setToCreatedAt] = useState<string | undefined>(undefined);
  const [fromCreatedAt, setFromCreatedAt] = useState<string | undefined>(
    undefined
  );
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(
    null
  );

  const [taxCertificateFilterParams, setTaxCertificateFilterParams] = useState<
    GetTaxCertificateListPayload
  >({});

  const onChangedDate = useCallback(
    (
      date: MaterialUiPickersDate | null,
      field: 'fromPeriod' | 'toPeriod' | 'fromCreatedAt' | 'toCreatedAt'
    ) => {
      const transformedDate = date ? transformerDateField(date) : undefined;
      if (field === 'toPeriod') {
        setToPeriod(date);
        setTaxCertificateFilterParams((prev) => ({
          ...prev,
          period_to: transformedDate
        }));
      } else if (field === 'fromPeriod') {
        setFromPeriod(date);
        setTaxCertificateFilterParams((prev) => ({
          ...prev,
          period_from: transformedDate
        }));
      } else if (field === 'toCreatedAt') {
        setToCreatedAt(transformedDate);
        setTaxCertificateFilterParams((prev) => ({
          ...prev,
          date_created_to: transformedDate
        }));
      } else if (field === 'fromCreatedAt') {
        setFromCreatedAt(transformedDate);
        setTaxCertificateFilterParams((prev) => ({
          ...prev,
          date_created_from: transformedDate
        }));
      }
    },
    []
  );

  const onApplyFilter = (params?: GetTaxCertificateListPayload) => {
    // remove undefined values
    const cleanedParams = Object.fromEntries(
      Object.entries(params || {}).filter(
        ([, value]) => value !== undefined && value !== null && value !== ''
      )
    );

    if (selectedSupplier) {
      cleanedParams.supplier_id = selectedSupplier.id ?? null;
    } else {
      delete cleanedParams.supplier_id;
    }
    onHandleAppliedFilter();
    setTaxCertificateFilterParams({ ...cleanedParams, page: 1 });
    fetchTaxCertificateList({
      ...cleanedParams,
      page: 1
    });
  };

  const onClearFilter = () => {
    setSelectedSupplier(null);
    setFromPeriod(null);
    setToPeriod(null);
    setFromCreatedAt(undefined);
    setToCreatedAt(undefined);

    const resetParams: GetTaxCertificateListPayload = { page: 1 };
    setTaxCertificateFilterParams(resetParams);

    fetchTaxCertificateList(resetParams);

    onHandleAppliedFilter();
  };

  useEffect(() => {
    getSuppliers({ limit: 999 });
  }, [getSuppliers]);

  return (
    <Card style={{ marginTop: 10, padding: 15 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Autocomplete
                disabled={isSuppliersLoading}
                options={suppliers}
                value={selectedSupplier}
                onChange={(e: any, val) => {
                  setSelectedSupplier(val);
                }}
                getOptionLabel={(suppliers) => suppliers?.name ?? ''}
                renderInput={(params) => (
                  <span
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center'
                    }}
                  >
                    <Typography style={{ height: '1.83em', fontWeight: '500' }}>
                      Supplier
                    </Typography>
                    <TextField {...params} variant="outlined" />
                  </span>
                )}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              style={{ display: 'flex', justifyContent: 'center' }}
            >
              <QuarterDatePicker
                title="Filter by Period Date"
                fromDateValue={fromPeriod || null}
                toDateValue={toPeriod || null}
                onChangeFromDate={(date) => onChangedDate(date, 'fromPeriod')}
                onChangeToDate={(date) => onChangedDate(date, 'toPeriod')}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <DatePickerRangeComponent
                title="Filter by Created Date"
                fullwidth
                fromDateValue={fromCreatedAt || null}
                toDateValue={toCreatedAt || null}
                fromDateMax={dateToday()}
                onChangeFromDate={(date) =>
                  onChangedDate(date, 'fromCreatedAt')
                }
                onChangeToDate={(date) => onChangedDate(date, 'toCreatedAt')}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid
          item
          xs={12}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <span
            style={{
              display: 'flex',
              justifyContent: 'space-around',
              width: '300px'
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                onApplyFilter(taxCertificateFilterParams);
              }}
            >
              Apply Filter
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={onClearFilter}
            >
              Clear Filter
            </Button>
          </span>
        </Grid>
      </Grid>
    </Card>
  );
};

export const Toolbar = memo(Component);
